/**
 * @file supplier-groups.tsx
 * @module app/modules/uProcure/vendors_management/vendor-profile/supplier-groups
 * @description Vendor Profile > Groups: groups inside a supplier (e.g. its factories). Draft POs of
 *   the supplier are made per group and never mix groups; products, ledger and product display stay
 *   on the supplier. Each group can have its own contact person, credit limit, PO tonnage, TIN, VAT
 *   and bank accounts; anything left "Same as supplier" uses the supplier's. Changes need the
 *   "Edit supplier groups" right (the server says so in `canEdit`).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, Offcanvas, Row, Spinner } from "react-bootstrap";
import APICallService from "../../../../../api/apiCallService";
import { PURCHASEORDER } from "../../../../../api/apiEndPoints";
import { POManagement } from "../../../../../utils/constants";
import { BANKTYPES } from "../../../../../utils/staticJSON";
import { useWarehouse } from "../../../../context/WarehouseContext";
import { error, success } from "../../../../../Global/toast";

interface Bank {
  bankName: string;
  branchName: string | null;
  accountNumber: string;
  identifierCode: string;
  bankType: number | null;
  tigoNumber: string | null;
}
interface Contact {
  name: string | null;
  phoneCountry: string | null;
  phone: string | null;
  email: string | null;
}
type InUse<T> = { value: T; from: "group" | "supplier" };
interface Group {
  _id: string;
  name: string;
  active: boolean;
  /** products in this group (set in Product Catalog) */
  products?: number;
  own: { contact: Contact; creditLimit: number | null; tonnageTon: number | null; tinNumber: string | null; vatNumber: string | null; banks: Bank[] };
  inUse: {
    contactName: InUse<string | null>;
    phone: InUse<string | null>;
    email: InUse<string | null>;
    creditLimit: InUse<number | null>;
    tonnageTon: InUse<number | null>;
    tinNumber: InUse<string | null>;
    vatNumber: InUse<string | null>;
    banks: InUse<Bank[]>;
  };
}
interface Supplier {
  name: string;
  contact: Contact;
  creditLimit: number | null;
  tonnageTon: number | null;
  tinNumber: string | null;
  vatNumber: string | null;
  banks: Bank[];
}

/** editor state: one "same as supplier" switch per section */
interface Draft {
  name: string;
  sameContact: boolean;
  contact: { name: string; phoneCountry: string; phone: string; email: string };
  sameCredit: boolean;
  creditLimit: string;
  sameTonnage: boolean;
  tonnageTon: string;
  sameTin: boolean;
  tinNumber: string;
  sameVat: boolean;
  vatNumber: string;
  sameBanks: boolean;
  banks: { bankName: string; branchName: string; accountNumber: string; identifierCode: string; bankType: string; tigoNumber: string }[];
}

const tsh = (n: number | null) => (n == null ? null : `TSh ${n.toLocaleString("en-US")}`);
const s = (v: unknown) => (v == null ? "" : String(v));
const emptyBank = { bankName: "", branchName: "", accountNumber: "", identifierCode: "", bankType: "", tigoNumber: "" };
const bankLine = (b: Bank) =>
  [b.bankName, b.accountNumber, b.branchName, b.bankType ? `${BANKTYPES.find((t) => t.value === b.bankType)?.label ?? ""} ${b.tigoNumber ?? ""}`.trim() : ""].filter(Boolean).join(" · ");

const draftOf = (g: Group | null): Draft => {
  const o = g?.own;
  return {
    name: g?.name ?? "",
    sameContact: !o || (!o.contact.name && !o.contact.phone && !o.contact.email),
    contact: { name: s(o?.contact.name), phoneCountry: s(o?.contact.phoneCountry) || "+255", phone: s(o?.contact.phone), email: s(o?.contact.email) },
    sameCredit: o?.creditLimit == null,
    creditLimit: s(o?.creditLimit),
    sameTonnage: o?.tonnageTon == null,
    tonnageTon: s(o?.tonnageTon),
    sameTin: !o?.tinNumber,
    tinNumber: s(o?.tinNumber),
    sameVat: !o?.vatNumber,
    vatNumber: s(o?.vatNumber),
    sameBanks: !o?.banks.length,
    banks: o?.banks.length
      ? o.banks.map((b) => ({ bankName: b.bankName, branchName: s(b.branchName), accountNumber: b.accountNumber, identifierCode: b.identifierCode, bankType: s(b.bankType), tigoNumber: s(b.tigoNumber) }))
      : [{ ...emptyBank }],
  };
};

/** a value in use; "Same as supplier" when it comes from the supplier */
const Value = ({ v, show }: { v: InUse<unknown>; show?: string | null }) => (
  <>
    <span className={v.value == null || v.value === "" ? "text-gray-500" : "fw-600"}>{show ?? (v.value == null || v.value === "" ? "Not set" : String(v.value))}</span>
    {v.from === "supplier" && (
      <Badge bg="light" text="dark" className="ms-2 fw-500 fs-12">
        Same as supplier
      </Badge>
    )}
  </>
);

const SupplierGroups = ({ businessId, supplierName }: { businessId: string; supplierName?: string }) => {
  const { selectedWarehouse } = useWarehouse();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  /** the group being edited; "new" for a new one */
  const [editing, setEditing] = useState<Group | "new" | null>(null);
  const [draft, setDraft] = useState<Draft>(draftOf(null));
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState("");

  const call = (endpoint: string, body: object | null, path: object) =>
    new APICallService(endpoint, body ?? {}, path, "", "", "", POManagement, selectedWarehouse?.value).callAPI();

  useEffect(() => {
    if (!businessId) return;
    (async () => {
      setLoading(true);
      const res = await call(PURCHASEORDER.supplierGroups, null, { business: businessId });
      if (res && Array.isArray(res.groups)) {
        setSupplier(res.supplier);
        setGroups(res.groups);
        setCanEdit(Boolean(res.canEdit));
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, reload]);

  const open = (g: Group | "new") => {
    setDraft(draftOf(g === "new" ? null : g));
    setEditing(g);
  };
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const setBank = (i: number, patch: Partial<Draft["banks"][number]>) => setDraft((d) => ({ ...d, banks: d.banks.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));

  const save = async () => {
    if (!draft.name.trim()) {
      error("Enter the group name");
      return;
    }
    const banks = draft.sameBanks ? [] : draft.banks.filter((b) => b.bankName.trim() || b.accountNumber.trim() || b.identifierCode.trim());
    if (!draft.sameBanks && !banks.length) {
      error("Add a bank account, or switch on Same as supplier");
      return;
    }
    if (banks.some((b) => !b.bankName.trim() || !b.accountNumber.trim() || !b.identifierCode.trim())) {
      error("Each bank account needs the bank name, account number and identifier code");
      return;
    }
    // "same as supplier" sends empty values: the server keeps them empty (the supplier's are used)
    const body = {
      name: draft.name.trim(),
      contact: draft.sameContact ? { name: "", phoneCountry: "", phone: "", email: "" } : draft.contact,
      creditLimit: draft.sameCredit ? "" : draft.creditLimit,
      tonnageTon: draft.sameTonnage ? "" : draft.tonnageTon,
      tinNumber: draft.sameTin ? "" : draft.tinNumber,
      vatNumber: draft.sameVat ? "" : draft.vatNumber,
    };
    setSaving(true);
    const isNew = editing === "new";
    const saved = isNew
      ? await call(PURCHASEORDER.addSupplierGroup, body, { business: businessId })
      : await call(PURCHASEORDER.updateSupplierGroup, body, { business: businessId, group: (editing as Group)._id });
    const id = saved?._id ?? (isNew ? null : (editing as Group)._id);
    let ok = Boolean(saved && id);
    // bank accounts only when they changed (or the group had its own and goes back to the supplier's)
    const hadOwn = !isNew && (editing as Group).own.banks.length > 0;
    if (ok && (banks.length || hadOwn)) {
      const res = await call(
        PURCHASEORDER.updateSupplierGroup,
        { accounts: banks.map((b) => ({ ...b, bankType: b.bankType ? Number(b.bankType) : null })) },
        { business: businessId, group: id, part: "banks" }
      );
      ok = Array.isArray(res);
    }
    setSaving(false);
    if (ok) {
      success(isNew ? "Group added" : "Group saved");
      setEditing(null);
    }
    if (saved) setReload((n) => n + 1);
  };

  const toggle = async (g: Group) => {
    setSwitching(g._id);
    const res = await call(PURCHASEORDER.updateSupplierGroup, { active: !g.active }, { business: businessId, group: g._id, part: "status" });
    setSwitching("");
    if (res) {
      success(g.active ? `${g.name} switched off` : `${g.name} switched on`);
      setReload((n) => n + 1);
    }
  };

  const sameSwitch = (label: string, on: boolean, key: keyof Draft, supplierValue: string) => (
    <Form.Check
      type="switch"
      id={`sg-same-${String(key)}`}
      className="mb-2"
      checked={on}
      disabled={saving}
      onChange={(e) => set({ [key]: e.target.checked } as Partial<Draft>)}
      label={
        <span className="fs-14">
          {label}: same as supplier <span className="text-gray-600">({supplierValue || "not set"})</span>
        </span>
      }
    />
  );

  return (
    <Card className="border" data-testid="supplier-groups">
      <Card.Body>
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-4 mb-6">
          <div className="fs-14 text-gray-700" style={{ maxWidth: 720 }}>
            Groups split <span className="fw-600">{supplier?.name || supplierName || "this supplier"}</span>&apos;s draft POs, for example one per
            factory: a draft PO never mixes products of different groups. Products, the ledger and product display stay on the supplier. Any
            detail left &quot;Same as supplier&quot; uses the supplier&apos;s.
          </div>
          {canEdit && (
            <Button className="uw-btn uw-btn-primary" onClick={() => open("new")} data-testid="sg-add">
              Add group
            </Button>
          )}
        </div>
        {loading ? (
          <div className="text-center py-10">
            <Spinner animation="border" />
          </div>
        ) : !groups.length ? (
          <div className="text-center py-10 text-gray-600" data-testid="sg-empty">
            No groups. This supplier&apos;s draft POs work as before: one draft for all its products.
          </div>
        ) : (
          <Row className="g-5">
            {groups.map((g) => (
              <Col md={6} xl={4} key={g._id}>
                <Card className={`border h-100 ${g.active ? "" : "bg-light"}`} data-testid={`sg-card-${g._id}`}>
                  <Card.Body>
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-4">
                      <div>
                        <div className="fs-16 fw-bold">{g.name}</div>
                        <Badge bg={g.active ? "light-success" : "light-danger"} text={g.active ? "success" : "danger"} className="fs-12 mt-1">
                          {g.active ? "Active" : "Switched off"}
                        </Badge>
                      </div>
                      {canEdit && (
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="light-primary" onClick={() => open(g)} data-testid={`sg-edit-${g._id}`}>
                            Edit
                          </Button>
                          <Button size="sm" variant="light" disabled={switching === g._id} onClick={() => toggle(g)} data-testid={`sg-toggle-${g._id}`}>
                            {switching === g._id ? <Spinner animation="border" size="sm" /> : g.active ? "Switch off" : "Switch on"}
                          </Button>
                        </div>
                      )}
                    </div>
                    <dl className="row fs-14 mb-0">
                      <dt className="col-5 fw-500 text-gray-600">Products</dt>
                      <dd className="col-7" data-testid={`sg-products-${g._id}`}>
                        <span className={g.products ? "fw-600" : "text-gray-500"}>{g.products ? g.products : "None yet (set in Product Catalog)"}</span>
                      </dd>
                      <dt className="col-5 fw-500 text-gray-600">Contact person</dt>
                      <dd className="col-7">
                        <Value v={g.inUse.contactName} />
                      </dd>
                      <dt className="col-5 fw-500 text-gray-600">Phone</dt>
                      <dd className="col-7">
                        <Value v={g.inUse.phone} />
                      </dd>
                      <dt className="col-5 fw-500 text-gray-600">Email</dt>
                      <dd className="col-7 text-break">
                        <Value v={g.inUse.email} />
                      </dd>
                      <dt className="col-5 fw-500 text-gray-600">Credit limit</dt>
                      <dd className="col-7">
                        <Value v={g.inUse.creditLimit} show={tsh(g.inUse.creditLimit.value)} />
                      </dd>
                      <dt className="col-5 fw-500 text-gray-600">PO tonnage</dt>
                      <dd className="col-7">
                        <Value v={g.inUse.tonnageTon} show={g.inUse.tonnageTon.value == null ? null : `${g.inUse.tonnageTon.value} tons`} />
                      </dd>
                      <dt className="col-5 fw-500 text-gray-600">TIN</dt>
                      <dd className="col-7">
                        <Value v={g.inUse.tinNumber} />
                      </dd>
                      <dt className="col-5 fw-500 text-gray-600">VAT</dt>
                      <dd className="col-7">
                        <Value v={g.inUse.vatNumber} />
                      </dd>
                      <dt className="col-5 fw-500 text-gray-600">Bank accounts</dt>
                      <dd className="col-7 mb-0">
                        {g.inUse.banks.value.length ? (
                          g.inUse.banks.value.map((b) => (
                            <div key={`${b.accountNumber}-${b.bankName}`} className="fw-600">
                              {bankLine(b)}
                            </div>
                          ))
                        ) : (
                          <span className="text-gray-500">Not set</span>
                        )}
                        {g.inUse.banks.from === "supplier" && (
                          <Badge bg="light" text="dark" className="fw-500 fs-12">
                            Same as supplier
                          </Badge>
                        )}
                      </dd>
                    </dl>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card.Body>

      <Offcanvas show={editing !== null} onHide={() => !saving && setEditing(null)} placement="end" style={{ width: 520 }} data-testid="sg-editor">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fs-20 fw-bold">{editing === "new" ? "Add group" : `Edit ${(editing as Group | null)?.name ?? ""}`}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form.Group className="mb-6">
            <Form.Label className="fs-14 fw-600">Group name *</Form.Label>
            <Form.Control value={draft.name} maxLength={80} placeholder="e.g. Mill Factory" onChange={(e) => set({ name: e.target.value })} data-testid="sg-name" />
          </Form.Group>

          <div className="fs-15 fw-bold mb-2">Contact person (the PO is shared with)</div>
          {sameSwitch("Contact", draft.sameContact, "sameContact", supplier?.contact.name ?? "")}
          {!draft.sameContact && (
            <Row className="g-3 mb-2">
              <Col xs={12}>
                <Form.Control placeholder="Name" value={draft.contact.name} onChange={(e) => set({ contact: { ...draft.contact, name: e.target.value } })} />
              </Col>
              <Col xs={4}>
                <Form.Control placeholder="+255" value={draft.contact.phoneCountry} onChange={(e) => set({ contact: { ...draft.contact, phoneCountry: e.target.value } })} />
              </Col>
              <Col xs={8}>
                <Form.Control placeholder="Phone" value={draft.contact.phone} onChange={(e) => set({ contact: { ...draft.contact, phone: e.target.value } })} />
              </Col>
              <Col xs={12}>
                <Form.Control type="email" placeholder="Email" value={draft.contact.email} onChange={(e) => set({ contact: { ...draft.contact, email: e.target.value } })} />
              </Col>
            </Row>
          )}

          <div className="fs-15 fw-bold mt-6 mb-2">Credit limit (TSh)</div>
          {sameSwitch("Credit limit", draft.sameCredit, "sameCredit", tsh(supplier?.creditLimit ?? null) ?? "")}
          {!draft.sameCredit && (
            <Form.Control type="number" min={0} placeholder="e.g. 20000000" value={draft.creditLimit} onChange={(e) => set({ creditLimit: e.target.value })} data-testid="sg-credit" />
          )}
          <div className="fs-12 text-gray-600 mt-1">Over the limit is only a warning on the draft PO, never a block.</div>

          <div className="fs-15 fw-bold mt-6 mb-2">PO tonnage (tons)</div>
          {sameSwitch("Tonnage", draft.sameTonnage, "sameTonnage", supplier?.tonnageTon != null ? `${supplier.tonnageTon} tons` : "")}
          {!draft.sameTonnage && (
            <Form.Control type="number" min={0} step="0.1" placeholder="e.g. 10" value={draft.tonnageTon} onChange={(e) => set({ tonnageTon: e.target.value })} data-testid="sg-tonnage" />
          )}

          <div className="fs-15 fw-bold mt-6 mb-2">Tax numbers</div>
          {sameSwitch("TIN", draft.sameTin, "sameTin", supplier?.tinNumber ?? "")}
          {!draft.sameTin && <Form.Control className="mb-3" placeholder="TIN" value={draft.tinNumber} onChange={(e) => set({ tinNumber: e.target.value })} />}
          {sameSwitch("VAT", draft.sameVat, "sameVat", supplier?.vatNumber ?? "")}
          {!draft.sameVat && <Form.Control placeholder="VAT number" value={draft.vatNumber} onChange={(e) => set({ vatNumber: e.target.value })} />}

          <div className="fs-15 fw-bold mt-6 mb-2">Bank accounts</div>
          {sameSwitch("Bank accounts", draft.sameBanks, "sameBanks", (supplier?.banks ?? []).map(bankLine).join("; "))}
          {!draft.sameBanks && (
            <>
              {draft.banks.map((b, i) => (
                <div key={i} className="border rounded p-4 mb-3" data-testid={`sg-bank-${i}`}>
                  <Row className="g-3">
                    <Col xs={6}>
                      <Form.Control placeholder="Bank name *" value={b.bankName} onChange={(e) => setBank(i, { bankName: e.target.value })} />
                    </Col>
                    <Col xs={6}>
                      <Form.Control placeholder="Branch" value={b.branchName} onChange={(e) => setBank(i, { branchName: e.target.value })} />
                    </Col>
                    <Col xs={6}>
                      <Form.Control placeholder="Account number *" value={b.accountNumber} onChange={(e) => setBank(i, { accountNumber: e.target.value })} />
                    </Col>
                    <Col xs={6}>
                      <Form.Control placeholder="Identifier code *" value={b.identifierCode} onChange={(e) => setBank(i, { identifierCode: e.target.value })} />
                    </Col>
                    <Col xs={6}>
                      <Form.Select value={b.bankType} onChange={(e) => setBank(i, { bankType: e.target.value })}>
                        <option value="">Mobile money (optional)</option>
                        {BANKTYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col xs={6}>
                      {b.bankType && <Form.Control placeholder="Mobile money number" value={b.tigoNumber} onChange={(e) => setBank(i, { tigoNumber: e.target.value })} />}
                    </Col>
                  </Row>
                  {draft.banks.length > 1 && (
                    <Button size="sm" variant="link" className="text-danger p-0 mt-2" onClick={() => set({ banks: draft.banks.filter((_, j) => j !== i) })}>
                      Remove this account
                    </Button>
                  )}
                </div>
              ))}
              <Button size="sm" variant="light-primary" onClick={() => set({ banks: [...draft.banks, { ...emptyBank }] })}>
                Add another account
              </Button>
            </>
          )}

          <div className="d-flex justify-content-end gap-3 mt-8 pt-4 border-top">
            <Button variant="light" disabled={saving} onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button className="uw-btn uw-btn-primary" disabled={saving} onClick={save} data-testid="sg-save">
              {saving ? <Spinner animation="border" size="sm" /> : editing === "new" ? "Add group" : "Save"}
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Card>
  );
};

export default SupplierGroups;
